import { useCallback, useId, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import clsx from 'clsx';
import './DataTable.css';

/* ------------------------------------------------------------------ *
 * The table is the product.
 *
 * Not a wrapper over a grid library: a semantic <table> with a small,
 * explicit column contract. Reasons, in order of how much they cost to
 * get wrong later:
 *
 *  1. Every row carries a handling-class band and can carry a tone.
 *     That is the visual system, and it has to be a first-class part
 *     of the row API, not a `renderCell` hack in every module.
 *  2. It prints. Picking lists and manifests go to a warehouse
 *     printer, and a virtualised canvas grid does not print.
 *  3. Keyboard: up/down move the cursor, Enter opens, Space selects.
 *     Warehouse and buying staff do not use a mouse.
 *
 * When a tenant genuinely needs row grouping or tree data, a licensed
 * grid can be slotted in behind this same interface for that one
 * screen. The interface is the asset; the renderer is replaceable.
 * ------------------------------------------------------------------ */

export interface Column<T> {
  key: string;
  /** Rendered in the stencil head. Keep it short; Spanish runs long. */
  header: ReactNode;
  /** `num` right-aligns and reserves mono width. */
  align?: 'start' | 'end' | 'num';
  /** CSS grid-ish width: '1fr', '120px', 'minmax(90px, 1fr)'. */
  width?: string;
  /** Hidden below this viewport width. The market floor is a phone. */
  hideBelow?: 'sm' | 'md' | 'lg';
  sortable?: boolean;
  cell: (row: T) => ReactNode;
  /** Value used by the built-in sorter when `sortable`. */
  sortValue?: (row: T) => string | number | null;
  /** Column head tooltip, for anything a three-letter stencil can't say. */
  title?: string;
}

/**
 * Optional members are written `?: T | undefined` throughout the kit.
 * The project runs `exactOptionalPropertyTypes`, which is worth keeping
 * — it catches genuinely absent-vs-undefined bugs in the domain types —
 * but presentation props are computed conditionally at nearly every
 * call site, and forcing a spread there buys nothing.
 */
export interface RowMeta {
  /** Drives the leading band colour. */
  handlingClass?: string | undefined;
  /** Drives a whole-row tint — used sparingly, e.g. a cancelled order. */
  tone?: 'ok' | 'warn' | 'alert' | 'info' | 'neutral' | undefined;
  /** Row is struck through — cancelled, void. */
  struck?: boolean | undefined;
  /** Highlighted for one beat after a live update. */
  flash?: boolean | undefined;
}

export type SortDirection = 'asc' | 'desc';
export interface SortState {
  key: string;
  direction: SortDirection;
}

interface DataTableProps<T> {
  rows: readonly T[];
  columns: readonly Column<T>[];
  rowKey: (row: T) => string;
  rowMeta?: (row: T) => RowMeta;
  onRowActivate?: (row: T) => void;
  /** Controlled selection. Omit for a non-selectable table. */
  selection?: {
    selected: ReadonlySet<string>;
    onChange: (next: Set<string>) => void;
  };
  sort?: SortState | null;
  onSortChange?: (next: SortState | null) => void;
  /** When true the sorter runs client-side. Server-sorted lists pass false. */
  clientSort?: boolean;
  activeRowKey?: string | null;
  loading?: boolean;
  /** Rendered instead of rows when there are none. */
  empty?: ReactNode;
  /** Sticky totals strip under the head. */
  summary?: ReactNode;
  caption?: string;
  className?: string;
}

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  rowMeta,
  onRowActivate,
  selection,
  sort = null,
  onSortChange,
  clientSort = true,
  activeRowKey = null,
  loading = false,
  empty,
  summary,
  caption,
  className,
}: DataTableProps<T>) {
  const tableId = useId();
  const bodyRef = useRef<HTMLTableSectionElement>(null);
  const [cursor, setCursor] = useState(0);

  const sorted = useMemo(() => {
    if (!clientSort || !sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortValue) return rows;
    const dir = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = col.sortValue!(a);
      const bv = col.sortValue!(b);
      if (av === null) return 1;
      if (bv === null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [rows, columns, sort, clientSort]);

  const toggleSort = useCallback(
    (key: string) => {
      if (!onSortChange) return;
      if (sort?.key !== key) onSortChange({ key, direction: 'asc' });
      else if (sort.direction === 'asc') onSortChange({ key, direction: 'desc' });
      else onSortChange(null);
    },
    [sort, onSortChange],
  );

  const allSelected =
    selection !== undefined && sorted.length > 0 &&
    sorted.every((r) => selection.selected.has(rowKey(r)));

  const toggleAll = () => {
    if (!selection) return;
    selection.onChange(allSelected ? new Set() : new Set(sorted.map(rowKey)));
  };

  const toggleOne = (key: string) => {
    if (!selection) return;
    const next = new Set(selection.selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    selection.onChange(next);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTableSectionElement>) => {
    if (sorted.length === 0) return;
    const move = (delta: number) => {
      e.preventDefault();
      const next = Math.max(0, Math.min(sorted.length - 1, cursor + delta));
      setCursor(next);
      bodyRef.current?.querySelectorAll<HTMLTableRowElement>('tr')[next]?.focus();
    };
    switch (e.key) {
      case 'ArrowDown': move(1); break;
      case 'ArrowUp': move(-1); break;
      case 'PageDown': move(10); break;
      case 'PageUp': move(-10); break;
      case 'Home': move(-sorted.length); break;
      case 'End': move(sorted.length); break;
      case 'Enter': {
        const row = sorted[cursor];
        if (row && onRowActivate) { e.preventDefault(); onRowActivate(row); }
        break;
      }
      case ' ': {
        const row = sorted[cursor];
        if (row && selection) { e.preventDefault(); toggleOne(rowKey(row)); }
        break;
      }
      default: break;
    }
  };

  const colCount = columns.length + (selection ? 1 : 0) + 1; // +1 for the band cell

  return (
    <div className={clsx('pd-table-wrap', className)} data-loading={loading || undefined}>
      <table className="pd-table" aria-busy={loading || undefined}>
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        <colgroup>
          <col style={{ inlineSize: 'var(--band-w)' }} />
          {selection ? <col style={{ inlineSize: '34px' }} /> : null}
          {columns.map((c) => (
            <col key={c.key} style={c.width ? { inlineSize: c.width } : undefined} />
          ))}
        </colgroup>

        <thead className="pd-table__head">
          <tr>
            <th className="pd-table__band-cell" aria-hidden="true" />
            {selection ? (
              <th scope="col" className="pd-table__select-cell">
                <input
                  type="checkbox"
                  className="pd-check"
                  checked={allSelected}
                  onChange={toggleAll}
                  aria-label="Select all rows"
                />
              </th>
            ) : null}
            {columns.map((c) => {
              const active = sort?.key === c.key;
              return (
                <th
                  key={c.key}
                  scope="col"
                  className="stencil pd-table__th"
                  data-align={c.align ?? 'start'}
                  data-hide-below={c.hideBelow}
                  aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                  title={c.title}
                >
                  {c.sortable && onSortChange ? (
                    <button
                      type="button"
                      className="pd-table__sort"
                      onClick={() => toggleSort(c.key)}
                      data-active={active || undefined}
                    >
                      {c.header}
                      <span className="pd-table__sort-mark" aria-hidden="true">
                        {active ? (sort.direction === 'asc' ? '↑' : '↓') : '·'}
                      </span>
                    </button>
                  ) : (
                    c.header
                  )}
                </th>
              );
            })}
          </tr>
          {summary ? (
            <tr className="pd-table__summary">
              <td colSpan={colCount}>{summary}</td>
            </tr>
          ) : null}
        </thead>

        <tbody
          ref={bodyRef}
          className="pd-table__body"
          onKeyDown={onKeyDown}
          aria-describedby={tableId}
        >
          {loading && sorted.length === 0
            ? Array.from({ length: 8 }, (_, i) => (
                <tr key={`hatch-${i}`} className="pd-table__row" aria-hidden="true">
                  <td className="pd-table__band-cell" />
                  {selection ? <td /> : null}
                  {columns.map((c) => (
                    <td key={c.key} data-align={c.align ?? 'start'} data-hide-below={c.hideBelow}>
                      <span className="hatch" style={{ display: 'block', blockSize: '0.9em' }} />
                    </td>
                  ))}
                </tr>
              ))
            : sorted.map((row, index) => {
                const key = rowKey(row);
                const meta = rowMeta?.(row) ?? {};
                const isSelected = selection?.selected.has(key) ?? false;
                return (
                  <tr
                    key={key}
                    className={clsx('pd-table__row', meta.flash && 'flash')}
                    tabIndex={index === cursor ? 0 : -1}
                    data-hc={meta.handlingClass}
                    data-tone={meta.tone}
                    data-struck={meta.struck || undefined}
                    data-selected={isSelected || undefined}
                    data-active={key === activeRowKey || undefined}
                    aria-selected={selection ? isSelected : undefined}
                    onFocus={() => setCursor(index)}
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest('input,button,a')) return;
                      onRowActivate?.(row);
                    }}
                    onDoubleClick={() => onRowActivate?.(row)}
                  >
                    <td className="pd-table__band-cell">
                      <span className="pd-band" />
                    </td>
                    {selection ? (
                      <td className="pd-table__select-cell">
                        <input
                          type="checkbox"
                          className="pd-check"
                          checked={isSelected}
                          onChange={() => toggleOne(key)}
                          aria-label={`Select row ${key}`}
                        />
                      </td>
                    ) : null}
                    {columns.map((c) => (
                      <td
                        key={c.key}
                        data-align={c.align ?? 'start'}
                        data-hide-below={c.hideBelow}
                      >
                        {c.cell(row)}
                      </td>
                    ))}
                  </tr>
                );
              })}
        </tbody>
      </table>

      {!loading && sorted.length === 0 ? <div className="pd-table__empty">{empty}</div> : null}
      <p id={tableId} className="sr-only">
        Use arrow keys to move between rows, Enter to open, Space to select.
      </p>
    </div>
  );
}
